import React from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { revertAccDocumentToDraft, saveAccDocument } from '../../../services/mutations';
import {
  Button,
  CheckCircleIcon,
  ChevronLeftIcon,
  LockIcon,
  PencilIcon,
  UndoIcon,
  useConfirm,
} from '../../../components/ui';
import { useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { acctFieldLabel } from '../shared/typography';
import { accountingReturnState } from '../shared/navigation';
import { DocumentLinesView } from '../shared/DocumentLinesView';
import { JournalBalanceBar } from '../shared/JournalBalanceBar';
import EntityAttachmentsSection from '../../../components/EntityAttachmentsSection';
import { isBalanced, perCompanyBalances, sumDebitCredit, sumDebitCreditUsd } from '../shared/balanceUtils';
import { useAccDocument } from './useData';
import { moduleConfig } from './config';
import { resolveDocumentEditRoute } from './formTemplates';

const statusBadgeClass = (status: string, isDark: boolean) => {
  if (status === 'DRAFT') {
    return isDark ? 'bg-slate-700/80 text-slate-200' : 'bg-slate-200/80 text-slate-700';
  }
  if (status === 'TEMP') {
    return isDark ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-200/80 text-amber-900';
  }
  return isDark ? 'bg-emerald-900/50 text-emerald-200' : 'bg-emerald-100 text-emerald-800';
};

const formatJournalStatus = (status: string) => {
  if (status === 'DRAFT') return 'Draft';
  if (status === 'TEMP') return 'Temp';
  if (status === 'SAVED') return 'Saved';
  return status;
};

const HeaderField: React.FC<{ label: string; value: string; className?: string }> = ({ label, value, className }) => {
  const { colors } = useTheme();
  return (
    <div
      className={cn(
        'min-w-0 rounded-lg border px-3 py-2.5',
        colors.border,
        colors.headerBg,
        className,
      )}
    >
      <p className={cn(acctFieldLabel, 'mb-0', colors.text)}>{label}</p>
      <p className={cn('mt-1 text-sm font-medium leading-snug', colors.text)} title={value}>
        {value}
      </p>
    </div>
  );
};

export const Detail: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const goBack = useNavigateBack(moduleConfig.module.route.list, getLocationReturnPath(location.state));
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const confirm = useConfirm();
  const docId = Number(id);
  const { item, isLoading, mutate } = useAccDocument(docId);

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[240px] flex-1 items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (!item) {
    return (
      <div className={cn('flex min-h-[240px] flex-1 items-center justify-center text-sm', colors.textMuted)}>
        Not found
      </div>
    );
  }

  const docLines = item.lines ?? [];
  const balanceLines = docLines.map((l) => ({
    companyId: l.companyId,
    debitLocal: l.debitLocal,
    creditLocal: l.creditLocal,
  }));
  const usdBalanceLines = docLines.map((l) => ({
    debitUSD: l.debitUSD ?? 0,
    creditUSD: l.creditUSD ?? 0,
  }));
  const totals = sumDebitCredit(balanceLines);
  const usdTotals = sumDebitCreditUsd(usdBalanceLines);
  const companyTotals = perCompanyBalances(balanceLines);
  const balanced = isBalanced(balanceLines);
  const companyNames = Object.fromEntries(
    docLines
      .filter((l) => l.company)
      .map((l) => [l.companyId, `${l.company!.name}`]),
  );

  const runAction = async (action: 'save' | 'revert') => {
    const label = item.code || `Draft #${item.id}`;
    const ok = await confirm(
      action === 'save'
        ? {
            title: 'Save journal',
            message: `Save “${label}”? Saved journals can no longer be edited until reverted to draft.`,
            confirmText: 'Save',
            cancelText: 'Cancel',
          }
        : {
            title: 'Revert to draft',
            message: `Revert “${label}” to draft? You will be able to edit it again.`,
            confirmText: 'Revert',
            cancelText: 'Cancel',
            confirmVariant: 'danger',
          },
    );
    if (!ok) return;
    try {
      if (action === 'save') await saveAccDocument(docId);
      if (action === 'revert') await revertAccDocumentToDraft(docId);
      toast.success(action === 'save' ? 'Journal saved' : 'Journal reverted to draft');
      mutate();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : null;
      toast.error(msg || 'Action failed');
    }
  };

  return (
    <div className="-mt-1 flex h-full min-h-0 flex-col">
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <div
          className={cn(
            'flex flex-shrink-0 items-center justify-between gap-3 border-b px-4 py-3 sm:px-6',
            colors.border,
            colors.headerBg,
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            <Button type="button" variant="ghost" size="icon" title="Back" aria-label="Back" onClick={goBack}>
              <ChevronLeftIcon className="h-5 w-5" />
            </Button>
            <span className="inline-flex items-center gap-1.5">
              <span
                className={cn(
                  'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wide',
                  statusBadgeClass(item.status, isDark),
                )}
              >
                {formatJournalStatus(item.status)}
              </span>
              {item.periodLocked && (
                <span
                  title="Accounting period is locked"
                  className={cn('inline-flex', isDark ? 'text-amber-400' : 'text-amber-600')}
                >
                  <LockIcon className="h-4 w-4" />
                </span>
              )}
            </span>
            <h1 className={cn('truncate text-base font-semibold sm:text-lg', colors.text)}>
              {item.code || `Draft #${item.id}`}
            </h1>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {(item.status === 'DRAFT' || item.status === 'TEMP') && (
              <>
                <Link
                  to={resolveDocumentEditRoute(item.documentType, item.id, moduleConfig.module.route.edit(item.id))}
                  state={accountingReturnState(location.pathname, location.search)}
                  className={cn(
                    'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
                    colors.primaryFg,
                    colors.interactiveHover,
                  )}
                  title="Edit"
                  aria-label="Edit"
                >
                  <PencilIcon className="h-5 w-5" />
                </Link>
                <Button
                  type="button"
                  variant="default"
                  size="icon"
                  title="Save"
                  aria-label="Save"
                  onClick={() => runAction('save')}
                >
                  <CheckCircleIcon className="h-5 w-5" />
                </Button>
              </>
            )}
            {item.status === 'SAVED' && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                title="Revert to draft"
                aria-label="Revert to draft"
                onClick={() => runAction('revert')}
              >
                <UndoIcon className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>

        <div className="dashboard-scrollbar min-h-0 flex-1 overflow-y-auto">
          <section className={cn('border-b px-4 py-4 sm:px-6', colors.border)}>
            <div className="flex min-w-0 flex-row flex-nowrap items-stretch gap-3 overflow-x-auto">
              <HeaderField
                label="Description"
                value={item.description || '—'}
                className="min-w-[12rem] flex-[1_1_16rem]"
              />
              <HeaderField
                label="Type"
                value={item.documentType?.name ?? item.documentType?.code ?? String(item.documentTypeId)}
                className="w-36 shrink-0"
              />
              <HeaderField
                label="Doc. date"
                value={formatDateGlobal(item.documentDate ?? undefined)}
                className="w-[10.5rem] shrink-0"
              />
              <HeaderField
                label="Posting date"
                value={formatDateGlobal(item.postingDate ?? undefined)}
                className="w-[10.5rem] shrink-0"
              />
            </div>
          </section>

          <section className={cn('border-b px-4 py-5 sm:px-6', colors.border)}>
            <h2 className={cn('mb-3 text-sm font-semibold', colors.text)}>Attachments</h2>
            <EntityAttachmentsSection
              downloadEndpoint=""
              showBulkDownload={false}
              files={(item.attachments ?? []).map((f) => ({
                id: f.id,
                fileName: f.fileName,
                fileSize: f.fileSize ?? null,
                fileType: f.mimeType ?? null,
                uploadedAt: f.uploadedAt ?? null,
              }))}
              getViewFileUrl={(fileId) => `/acc-documents/${item.id}/files/${fileId}`}
            />
          </section>

          <section className="px-4 py-5 sm:px-6">
            <DocumentLinesView lines={docLines} />
          </section>
        </div>

        <div className="flex-shrink-0">
          <JournalBalanceBar
            debitLocal={totals.debit}
            creditLocal={totals.credit}
            debitUSD={usdTotals.debit}
            creditUSD={usdTotals.credit}
            diff={totals.diff}
            balanced={balanced}
            companyTotals={companyTotals}
            companyNames={companyNames}
          />
        </div>
      </div>
    </div>
  );
};
